import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';

interface LocationModalProps {
  show: boolean;
  onLocationObtained: (lat: number, lng: number, locationStr: string) => void;
}

const LocationModal: React.FC<LocationModalProps> = ({ show, onLocationObtained }) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const requestLocation = () => {
    setLoading(true);
    setErrorMsg(null);

    if (!navigator.geolocation) {
      setErrorMsg('Geolocation is not supported by your browser.');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLoading(false);
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const locationStr = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        onLocationObtained(lat, lng, locationStr);
      },
      (err) => {
        setLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setErrorMsg('Location permission denied. Please allow location access in your browser site settings and click Retry.');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setErrorMsg('Location information unavailable. Please check your network/GPS and retry.');
        } else if (err.code === err.TIMEOUT) {
          setErrorMsg('Location request timed out. Please click Retry.');
        } else {
          setErrorMsg(err.message || 'Failed to capture location.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (show) {
      requestLocation();
    }
  }, [show]);

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      className="location-modal"
    >
      <Modal.Header className="bg-primary text-white border-0 py-3">
        <Modal.Title className="fw-bold fs-5 d-flex align-items-center gap-2">
          <i className="fas fa-map-marker-alt text-warning"></i> Location Access Required
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4 text-center">
        <div className="mb-3">
          <div
            className="mx-auto rounded-circle bg-light text-primary d-flex align-items-center justify-content-center mb-3"
            style={{ width: '64px', height: '64px' }}
          >
            <i className="fas fa-street-view" style={{ fontSize: '28px' }}></i>
          </div>
          <h5 className="fw-bold text-dark">Staff Daily Check-In Verification</h5>
          <p className="text-muted small mb-0" style={{ lineHeight: 1.5 }}>
            All staff employees are required to verify their location to record daily attendance and check-in time.
          </p>
        </div>

        {errorMsg && (
          <Alert variant="danger" className="text-start small rounded-3 my-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {errorMsg}
          </Alert>
        )}

        {loading ? (
          <div className="py-3">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2 text-muted small">Capturing your GPS location...</div>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={requestLocation}
            className="w-100 py-2.5 fw-bold text-white shadow-sm mt-2"
            style={{ borderRadius: '10px', background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)', border: 'none' }}
          >
            <i className="fas fa-location-arrow me-2"></i> Allow Location & Check In
          </Button>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LocationModal;
